import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { findIndex, get, noop } from 'lodash-es';
import { Button } from 'reactstrap';
/* Constants */
import { CAPITAL_ONE_LOGO_SRC } from 'client/site-modules/financing/constants/financing';
import { BASICS_STEP, STEPS } from 'site-modules/shared/constants/financing/steps';
import { Location } from 'client/site-modules/financing/constants/models';
/* Utils */
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { updateCreativeId } from 'site-modules/financing/utils/update-creative-id';
/* Components */
import { NavigationStep } from './navigation-step';

import './steps-navigation.scss';

/**
 * StepsNavigation is a container for each step like basics, personal and etc.
 */
export function StepsNavigation({
  activeStep,
  location,
  lastCompletedStep = '',
  enabledNextBtn = false,
  isMobile = false,
  goToNextScreen = noop,
  goToPrevScreen = noop,
  showBackBtn = false,
  setModelValue,
  vin = null,
  isMatchingVIN = false,
  isPreVinFlow = false,
}) {
  const nextBtn = classes => (
    <Button
      disabled={!enabledNextBtn}
      color="link"
      className={classNames(classes, 'medium next-btn text-capitalize p-0 text-decoration-none', {
        'text-gray-darker': enabledNextBtn,
        'text-gray-lighter': !enabledNextBtn,
      })}
      onClick={goToNextScreen}
    >
      <span>Next </span>
      <i className={classNames('icon-arrow-right4', { small: !isMobile, 'size-16': isMobile })} aria-hidden />
    </Button>
  );

  const backBtn = classes => (
    <Button
      color="link"
      className={classNames(classes, 'medium text-capitalize p-0 text-decoration-none text-gray-darker back-btn')}
      onClick={goToPrevScreen}
    >
      <i className={classNames('icon-arrow-left4', { small: !isMobile, 'size-16': isMobile })} aria-hidden />{' '}
      <span>Back</span>
    </Button>
  );

  const steps = STEPS.filter(step => get(step, 'showNavigation'));
  const isBasicsStep = activeStep === BASICS_STEP;

  return (
    <div
      data-tracking-parent={updateCreativeId({ isPreVinFlow, isMatchingVIN, trackingParent: 'financing-navigation' })}
    >
      <div className="pos-r">
        {showBackBtn && backBtn('hidden-sm-down')}

        {isBasicsStep ? (
          <h1 className="size-24 fw-bold text-center mt-2 mb-1_5">Pre-qualify for Financing</h1>
        ) : (
          <h1>
            <img
              className="d-block mx-auto mt-2 mb-1_5"
              width={111}
              height={40}
              src={getStaticImageUrl(CAPITAL_ONE_LOGO_SRC)}
              alt="Capital One"
            />
          </h1>
        )}

        {nextBtn('hidden-sm-down')}
      </div>

      <ol
        className="steps-navigation-container mx-0_5 mx-md-auto list-unstyled d-flex justify-content-between"
        aria-label="Steps"
      >
        {steps.map((step, index) => (
          <NavigationStep
            key={step.path}
            index={index}
            step={step}
            activeStep={activeStep}
            location={location}
            lastVisitedIndex={findIndex(steps, ['path', lastCompletedStep])}
            activeStepIndex={findIndex(steps, ['path', activeStep])}
            isMobile={isMobile}
            setModelValue={setModelValue}
            vin={vin}
          />
        ))}
      </ol>
      <div className="mt-1_5 text-center pos-r d-sm-none d-xs-block">
        {backBtn('hidden-sm-up')}
        <div className="small">{`Step ${findIndex(steps, ['path', activeStep]) + 1} of ${steps.length}`}</div>
        <div className="text-primary fw-bold text-capitalize">{activeStep}</div>
        {nextBtn('hidden-sm-up')}
      </div>
    </div>
  );
}

StepsNavigation.propTypes = {
  activeStep: PropTypes.string.isRequired,
  location: Location.isRequired,
  setModelValue: PropTypes.func.isRequired,
  vin: PropTypes.string,
  lastCompletedStep: PropTypes.string,
  enabledNextBtn: PropTypes.bool,
  isMobile: PropTypes.bool,
  goToNextScreen: PropTypes.func,
  goToPrevScreen: PropTypes.func,
  showBackBtn: PropTypes.bool,
  isMatchingVIN: PropTypes.bool,
  isPreVinFlow: PropTypes.bool,
};
